import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import impuestoService, { ImpuestoReadDto } from '../../services/impuestoService';

const labelTipo = (tipo?: string | null): string => {
  const t = (tipo || '').trim().toUpperCase();
  if (t === 'C' || t === 'COMPRA') return 'Compra';
  if (t === 'V' || t === 'VENTA') return 'Venta';
  return tipo?.trim() || '-';
};

const Impuestos: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [impuestos, setImpuestos] = useState<ImpuestoReadDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchImpuestos = async (pageNum: number = page, pageSize: number = rowsPerPage, search: string = searchTerm) => {
    try {
      setLoading(true);
      const data = await impuestoService.getImpuestos({
        pageNumber: pageNum + 1,
        pageSize: pageSize,
        searchTerm: search || undefined
      });
      setImpuestos(data.items);
      setTotalCount(data.totalCount);
    } catch (error: any) {
      console.error('Error al cargar impuestos:', error);
      enqueueSnackbar('Error al cargar impuestos', { variant: 'error' });
      setImpuestos([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImpuestos();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchImpuestos(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchImpuestos(newPage, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchImpuestos(0, newRowsPerPage, searchTerm);
  };

  const handleDelete = async (codigoImpuesto: string) => {
    if (window.confirm('¿Está seguro de eliminar este impuesto?')) {
      try {
        await impuestoService.deleteImpuesto(codigoImpuesto);
        enqueueSnackbar('Impuesto eliminado exitosamente', { variant: 'success' });
        fetchImpuestos(page, rowsPerPage, searchTerm);
      } catch (error: any) {
        console.error('Error al eliminar impuesto:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar impuesto', { variant: 'error' });
      }
    }
  };

  const handleEdit = (imp: ImpuestoReadDto) => {
    const codigo = (imp.codigoImpuesto || '').trim();
    if (codigo) navigate(`/configuraciones/impuestos/${encodeURIComponent(codigo)}`);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Impuestos
            </Typography>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/configuraciones/impuestos/nuevo')}>
              Nuevo Impuesto
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por código o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {loading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <Typography color="text.secondary">Cargando...</Typography>
            </Box>
          ) : isMobile ? (
            <Stack spacing={2}>
              {impuestos.map((imp) => {
                const codigo = (imp.codigoImpuesto || (imp as any).codigoImpuesto || '').trim();
                const desc = (imp.descripcion || (imp as any).descripcion || '').trim();
                const tipo = labelTipo(imp.tipo || (imp as any).Tipo);
                const cta = imp.ctaCtable ?? (imp as any).ctaCtable;
                const valor = imp.valor ?? (imp as any).valor;
                return (
                  <Card key={codigo} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {codigo} - {desc}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Tipo: {tipo} | Cuenta: {cta ?? '-'} | Valor: {valor != null ? valor : '-'}
                      </Typography>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton size="small" onClick={() => handleEdit(imp)} color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(codigo)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Cuenta</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Valor</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {impuestos.map((imp) => {
                    const codigo = (imp.codigoImpuesto || (imp as any).CodigoImpuesto || '').trim();
                    const desc = (imp.descripcion || (imp as any).Descripcion || '').trim();
                    const tipo = labelTipo(imp.tipo || (imp as any).Tipo);
                    const cta = imp.ctaCtable ?? (imp as any).CtaCtable;
                    const valor = imp.valor ?? (imp as any).Valor;
                    return (
                      <TableRow key={codigo} hover>
                        <TableCell>{codigo}</TableCell>
                        <TableCell>{desc}</TableCell>
                        <TableCell>{tipo}</TableCell>
                        <TableCell align="right">{cta ?? '-'}</TableCell>
                        <TableCell align="right">{valor != null ? valor : '-'}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => handleEdit(imp)} color="primary">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton size="small" onClick={() => handleDelete(codigo)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Impuestos;
